import React, { useEffect, useState, useCallback } from 'react'
import styles from './styles.module.css'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Table from '../../components/Table'
import { deleteGradeOption } from '../../actions/directory'
import Confirmation from '../../components/Confirmation/Confirmation'
import { getNewSchoolYear } from '../../actions/newSchoolYear'
import { Checkbox, Input, Select } from '../../components/ModalInputs'
import { getSchoolYears } from '../../actions/common'
import { mockTableData, initialFormValues } from './mockData'

const validationSchema = Yup.object({
  schoolYearId: Yup.string().required('Выберите учебный год'),
  orderDate: Yup.date().nullable(),
  orderNumber: Yup.string().max(25, 'Максимум 25 символов'),
  protocolDate: Yup.date().nullable(),
  protocolNumber: Yup.string().max(25, 'Максимум 25 символов')
})

export default function NewSchoolYear({ modal }) {
  const dispatch = useDispatch()
  const { schoolYears } = useSelector(state => state.common)

  const [tableData, setTableData] = useState(mockTableData)
  const [confirmation, setConfirmation] = useState(false)

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (formValues) => {
      dispatch(getNewSchoolYear({
        start_year_id: formValues.schoolYearId,
        end_year_id: Number(formValues.schoolYearId) + 1,
        edt_common_date: formValues.orderDate,
        edt_common_order: formValues.orderNumber,
        is_graduation: formValues.addGraduationStatus
      }))
    }
  })

  useEffect(() => {
    dispatch(getSchoolYears())
  }, [dispatch])

  const toggleSection = useCallback((indices) => {
    if (!indices) return

    setTableData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData))

      if (indices.length === 1) {
        newData[indices[0]].show = !newData[indices[0]].show
      } else {
        let item = newData[indices[0]]
        indices.slice(1).forEach(idx => {
          item = item.children[idx]
        })
        item.show = !item.show
      }

      return newData
    })
  }, [])

  const closeModal = () => setConfirmation(false)

  const acceptConfirmation = () => {
    dispatch(deleteGradeOption(confirmation.id))
    setConfirmation(false)
  }

  const renderOptions = (item) => {
    const { xp_key, name, title } = item
    const optionName = name || title
    const optionValue = xp_key
    return <option key={optionValue} value={optionValue}>{optionName}</option>
  }

  const renderGroupRow = (group, groupIndex) => (
    <React.Fragment key={group.id}>
      <tr
        className={styles.groupRow}
        onClick={() => toggleSection([groupIndex])}
      >
        <td className={styles.cellOldName}>{group.oldName}</td>
        <td className={styles.cellCenter}>{group.oldParallel}</td>
        <td className={styles.cellNewName}>{group.newName}</td>
        <td className={styles.cellCenter}>{group.newParallel || ''}</td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
      {group.show && group.children?.map((child) =>
        renderChildRow(child)
      )}
    </React.Fragment>
  )

  const renderChildRow = (child) => (
    <tr key={child.id} className={styles.childRow}>
      <td className={styles.cellOldNameChild}>
        <span className={styles.treeIndent}></span>
        {child.oldName}
      </td>
      <td className={styles.cellCenter}>{child.oldParallel}</td>
      <td className={styles.cellNewName}>{child.newName}</td>
      <td className={styles.cellCenter}>{child.newParallel || ''}</td>
      <td className={styles.cellCenter}>{child.periodType}</td>
      <td className={styles.cellCenter}>
        <input
          type="checkbox"
          checked={child.isGraduation}
          readOnly
          className={styles.checkboxCell}
        />
      </td>
      <td className={styles.cellCenter}>{child.date}</td>
      <td className={styles.cellCenter}>{child.orderNumber}</td>
      <td className={styles.cellCenter}>{child.orderDate}</td>
      <td className={styles.cellCenter}>{child.protocolNumber}</td>
      <td className={styles.cellCenter}>{child.protocolDate}</td>
    </tr>
  )

  const error = (fieldName) => touched[fieldName] && errors[fieldName]

  return (
    <div className={styles.wrapper} style={!modal ? { width: '90vw', margin: '0 auto' } : {}}>
      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Удалить ${confirmation.id}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={closeModal}
        />
      )}

      <h1>Новый учебный год</h1>

      <form onSubmit={handleSubmit} className={styles.form}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Основные настройки</h2>

          <div className={styles.formRow}>
            <Select
              label="Учебный год"
              name="schoolYearId"
              value={values.schoolYearId}
              onChange={handleChange}
              error={error('schoolYearId')}
              style={{ backgroundColor: 'white', minWidth: '200px' }}
            >
              <option value="">Выберите год</option>
              {schoolYears?.map(renderOptions)}
            </Select>
          </div>

          <div className={styles.checkboxGroup}>
            <div className={styles.checkboxColumn}>
              <Checkbox
                label="Переименовать классы (напр. 7А в 8А)"
                name="renameClasses"
                checked={values.renameClasses}
                onChange={handleChange}
              />
              <Checkbox
                label="Копировать учебный распорядок"
                name="copySchedule"
                checked={values.copySchedule}
                onChange={handleChange}
              />
              <Checkbox
                label="Обновить состав элективных курсов"
                name="updateElectives"
                checked={values.updateElectives}
                onChange={handleChange}
              />
            </div>

            <div className={styles.checkboxColumn}>
              <Checkbox
                label="Добавить статус «Перевод в след. класс»"
                name="addTransferStatus"
                checked={values.addTransferStatus}
                onChange={handleChange}
              />
              <Checkbox
                label="Добавить статус «Выпуск»"
                name="addGraduationStatus"
                checked={values.addGraduationStatus}
                onChange={handleChange}
              />
              <Checkbox
                label="Формировать группы по предметам"
                name="formSubjectGroups"
                checked={values.formSubjectGroups}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Реквизиты документов</h2>

          <div className={styles.documentsGrid}>
            <fieldset className={styles.fieldset}>
              <legend>Приказ</legend>
              <div className={styles.fieldsetContent}>
                <Input
                  type="date"
                  label="Дата приказа"
                  name="orderDate"
                  value={values.orderDate}
                  onChange={handleChange}
                  error={error('orderDate')}
                />
                <Input
                  label="Номер приказа"
                  name="orderNumber"
                  value={values.orderNumber}
                  onChange={handleChange}
                  error={error('orderNumber')}
                  maxLength={25}
                  placeholder="Например: 123-п"
                />
              </div>
            </fieldset>

            <fieldset className={styles.fieldset}>
              <legend>Протокол педсовета</legend>
              <div className={styles.fieldsetContent}>
                <Input
                  type="date"
                  label="Дата протокола"
                  name="protocolDate"
                  value={values.protocolDate}
                  onChange={handleChange}
                  error={error('protocolDate')}
                />
                <Input
                  label="Номер протокола"
                  name="protocolNumber"
                  value={values.protocolNumber}
                  onChange={handleChange}
                  error={error('protocolNumber')}
                  maxLength={25}
                  placeholder="Например: 45"
                />
              </div>
            </fieldset>
          </div>
        </section>

        <div className={styles.formActions}>
          <button type="submit" className={styles.submitButton}>
            Сформировать
          </button>
        </div>
      </form>

      <section className={styles.tableSection}>
        <Table className={styles.tableMKB}>
          <thead>
            <tr>
              <th>Старое название</th>
              <th>Старая параллель</th>
              <th>Новое название</th>
              <th>Новая параллель</th>
              <th>Тип периода</th>
              <th>Выпуск</th>
              <th>Дата</th>
              <th>Номер приказа</th>
              <th>Дата приказа</th>
              <th>Номер протокола</th>
              <th>Дата протокола</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((group, index) => renderGroupRow(group, index))}
          </tbody>
        </Table>
      </section>
    </div>
  )
}
